// src/oura/components/aeo/AnswerScreenshot.tsx
//
// The full-page capture of the real ChatGPT answer (#3564).
//
// This is the most convincing thing in the product: a client can open ChatGPT
// and see the same page, with their own position visible among named
// competitors. So it is shown cropped to its top, dated, and one press away
// from full size.
//
// Two hops, not one. The image endpoint needs the user's Authorization header,
// which an `<img src>` cannot send, so we ask for a signed URL and set THAT as
// the src. The URL dies after ~15 minutes, which a page left open will outlive,
// so it is refetched on demand rather than assumed good.

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { CameraIcon, ChevronRightIcon, Loader2Icon, Maximize2Icon, XIcon } from "lucide-react";
import { getAnswerScreenshotUrl } from "@service/aeo";
import { shortDate } from "../../lib/oura";
import { cn } from "../../lib/cn";
import { FOCUS_RING, PillButton, useEscapeLayer } from "@oura/ui";

const ALT = "The ChatGPT answer as it appeared, captured during the scan";

/**
 * A capture, cropped to its top, with the full-size view.
 *
 * The full-size view registers on the shared escape stack, so inside a
 * drawer the first Escape closes the picture and the second the drawer. It
 * is a dialog in its own right: its Close takes focus on open, Tab stays on
 * it, and focus goes back to "Open full size" on close.
 */
export function CaptureView({
    src,
    takenAt,
    onError,
    onOpen,
    compact = false,
}: {
    src: string;
    /**
     * A row with a thumbnail rather than the cropped picture: the question
     * drawer keeps the answer text as the thing to read and the capture one
     * press away, instead of a second copy of the answer under the first.
     */
    compact?: boolean;
    /**
     * When the picture was taken (ourteam-backend#4800). The date is part of
     * the claim: an answer is something an assistant said at a MOMENT, and a
     * capture with no date invites reading an old one as today's. Null on an
     * older scan, which shows the picture undated rather than dated by a guess.
     */
    takenAt?: string | null;
    /** A stale signed URL renders as a broken image; the owner renews it. */
    onError?: () => void;
    onOpen?: () => void;
}) {
    const [open, setOpen] = useState(false);
    const openerRef = useRef<HTMLButtonElement>(null);
    const close = () => {
        setOpen(false);
        openerRef.current?.focus();
    };
    useEscapeLayer(close, open);
    // Close is the one control inside: Tab would otherwise walk out into the
    // drawer under the overlay, which aria-modal says is out of reach.
    const trapTab = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== "Tab") return;
        e.preventDefault();
        e.currentTarget.querySelector<HTMLButtonElement>("button")?.focus();
    };
    const date = takenAt ? shortDate(takenAt.slice(0, 10)) : null;

    const opener = compact ? (
        <button
            ref={openerRef}
            type="button"
            onClick={() => { onOpen?.(); setOpen(true); }}
            aria-label="Open the screenshot full size"
            className={cn("group flex w-full items-center gap-3 rounded-xl p-1.5 pr-3 text-left transition-colors hover:bg-gray-50", FOCUS_RING)}
        >
            <span className="relative block h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-white ring-1 ring-black/[0.08]">
                <img src={src} alt="" onError={onError} loading="lazy" className="block h-full w-full object-cover object-top" />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">See it as ChatGPT showed it</span>
                <span className="block text-xs text-gray-500">{date ? `Screenshot from chatgpt.com, ${date}` : "Screenshot from chatgpt.com"}</span>
            </span>
            <ChevronRightIcon aria-hidden size={16} className="shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
        </button>
    ) : null;

    return (
        <div className={compact ? undefined : "mt-3"}>
            {opener}
            {!compact && (<>
            <p className="m-0 mb-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                <CameraIcon aria-hidden size={13} />
                Captured from chatgpt.com
                {date && <span className="text-gray-400">· {date}</span>}
            </p>
            <button
                ref={openerRef}
                type="button"
                onClick={() => { onOpen?.(); setOpen(true); }}
                aria-label="Open the screenshot full size"
                className={cn(
                    "relative block max-h-96 w-full cursor-zoom-in overflow-hidden rounded-xl bg-white text-left ring-1 ring-black/[0.08]",
                    FOCUS_RING,
                )}
            >
                <img src={src} alt={ALT} onError={onError} loading="lazy" className="block w-full object-cover object-top" />
                {/* The crop fades out rather than cutting a line in half. */}
                <span aria-hidden className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-white/0 to-white" />
                <span className="absolute bottom-2.5 right-2.5 inline-flex h-[26px] items-center gap-1.5 rounded-full bg-white px-2.5 text-xs font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.06)]">
                    <Maximize2Icon aria-hidden size={12} />
                    Open full size
                </span>
            </button>
            </>)}

            {open && createPortal(
                // Above the drawer (z-40), and on the body so no card clips it.
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Screenshot, full size"
                    onClick={close}
                    onKeyDown={trapTab}
                    className="fixed inset-0 z-50 cursor-zoom-out overflow-auto bg-black/70 px-4 py-6"
                >
                    {/* A phone shows the picture at its own width, scrolled:
                        fitted to 358px it came out smaller than the crop it
                        opened from. From 640px it fits the column. */}
                    <div className="w-max sm:mx-auto sm:w-full sm:max-w-3xl">
                        <div className="sticky left-0 mb-2 flex max-w-[calc(100vw-2rem)] items-center justify-between gap-3">
                            <p className="m-0 text-xs text-white/80">{date ? `Captured from chatgpt.com on ${date}` : "Captured from chatgpt.com"}</p>
                            {/* Focus lands here on open, the way a dialog takes it. */}
                            <PillButton autoFocus size="sm" icon={XIcon} onClick={close}>Close</PillButton>
                        </div>
                        <img src={src} alt={ALT} onError={onError} className="block max-w-none rounded-xl bg-white sm:w-full sm:max-w-full" />
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}

/** The live capture for one question: fetched, renewed on expiry, absent when there is none. */
export function AnswerScreenshot({
    assessmentId,
    n,
    takenAt,
    compact = false,
}: {
    assessmentId: string;
    compact?: boolean;
    /** The question's index number: the screenshot's address. */
    n: number;
    takenAt?: string | null;
}) {
    const [src, setSrc] = useState<string | null>(null);
    const [expiresAt, setExpiresAt] = useState(0);
    const [state, setState] = useState<"loading" | "ready" | "none">("loading");

    useEffect(() => {
        let cancelled = false;
        setState("loading");
        void getAnswerScreenshotUrl(assessmentId, n).then((got) => {
            if (cancelled) return;
            // null covers both "no screenshot for this question" and a failed
            // request. Neither is worth an error: the answer text is the point.
            if (!got) { setState("none"); return; }
            setSrc(got.url);
            setExpiresAt(got.expiresAt);
            setState("ready");
        });
        return () => { cancelled = true; };
    }, [assessmentId, n]);

    /**
     * Called from the image's own `onError` as well as on expiry, because a
     * URL can be rejected before its clock runs out. A renewal that comes back
     * with nothing drops the block rather than leaving a broken-image glyph.
     */
    const renew = () => {
        void getAnswerScreenshotUrl(assessmentId, n).then((got) => {
            if (!got) { setState("none"); return; }
            setSrc(got.url);
            setExpiresAt(got.expiresAt);
        });
    };

    if (state === "none" || (state === "ready" && !src)) return null;
    if (state === "loading" || !src) {
        return (
            <div className={cn("flex items-center justify-center rounded-xl bg-gray-50 text-xs text-gray-400", compact ? "h-[68px]" : "mt-3 h-24")}>
                <Loader2Icon aria-hidden size={14} className="mr-1.5 animate-spin" />
                Loading the screenshot…
            </div>
        );
    }
    return (
        <CaptureView
            src={src}
            takenAt={takenAt}
            onError={renew}
            onOpen={() => { if (Date.now() >= expiresAt) renew(); }}
            compact={compact}
        />
    );
}
